import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    Injector,
    input,
    OnInit,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
    AbstractControl,
    ControlValueAccessor,
    FormControl,
    NG_VALIDATORS,
    NgControl,
    ReactiveFormsModule,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CountryModel, TranslatedString } from 'lemunzo-api';
import {
    CountryCode,
    getExampleNumber,
    isValidPhoneNumber,
    parsePhoneNumberFromString,
} from 'libphonenumber-js/min';
import mobileExamples from 'libphonenumber-js/mobile/examples';
import { merge } from 'rxjs';
import { LocalizationService } from '../../services/localization.service';
import {
    PhoneCountryOption,
    PhoneCountryService,
} from '../../services/phone-country.service';
import { FlagComponent } from '../flag/flag.component';
import { TranslationComponent } from '../translation/translation.component';

const DEFAULT_LABEL_KEY = 'shared.phoneInput.label';

const INITIAL_PARENT_STATE: ParentState = {
    invalid: false,
    touched: false,
    dirty: false,
    errors: null,
};

type ParentState = {
    invalid: boolean;
    touched: boolean;
    dirty: boolean;
    errors: ValidationErrors | null;
};

/**
 * Phone number input that combines a country dial-code selector
 * and a nationally-formatted phone input into a single form control.
 *
 * The component is a `ControlValueAccessor`: it emits a single concatenated
 * string (e.g. `+3852236512366`, no spaces) to the parent form, or `''`
 * when empty. On `writeValue` it decodes an incoming string, auto-detects
 * the country, and splits the national part back into the input.
 *
 * Note: The component is not used outside of lemunzo-shared - thus it is not
 * exported from the `public-api.ts` file.
 */
@Component({
    selector: 'apcam-phone-input',
    imports: [
        FlagComponent,
        MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatMenuModule,
        ReactiveFormsModule,
        TranslatePipe,
        TranslationComponent,
    ],
    templateUrl: './phone-input.component.html',
    styleUrl: './phone-input.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: PhoneInputComponent,
            multi: true,
        },
    ],
})
export class PhoneInputComponent
    implements ControlValueAccessor, Validator, OnInit
{
    public readonly label = input<string | TranslatedString>(DEFAULT_LABEL_KEY);
    public readonly countryList = input<
        Map<string, CountryModel[]> | undefined
    >(undefined);
    public readonly allowedCountries = input<string[] | undefined>(undefined);
    public readonly priorityCountries = input<string[] | undefined>(undefined);
    public readonly required = input(false, { transform: booleanAttribute });

    /**
     * Disables the built-in synchronous validators (`invalidPhone`,`countryRequired`).
     */
    public readonly skipValidation = input(false, {
        transform: booleanAttribute,
    });
    public readonly showPlaceholder = input(true, {
        transform: booleanAttribute,
    });

    protected readonly nationalControl = new FormControl<string>('', {
        nonNullable: true,
    });
    protected readonly selectedCountryIso2 = signal<string | null>(null);

    protected readonly disabled = signal(false);
    protected readonly countryOptions = signal<PhoneCountryOption[]>([]);
    protected readonly placeholder = signal<string>('');
    protected readonly parentState = signal<ParentState>(INITIAL_PARENT_STATE);
    protected readonly selectedCountryIso2Option = computed(() => {
        const iso2 = this.selectedCountryIso2();
        if (!iso2) {
            return null;
        }

        return (
            this.countryOptions().find((option) => option.iso2 === iso2) ?? null
        );
    });

    protected readonly labelIsString = computed(
        () => typeof this.label() === 'string'
    );
    protected readonly labelText = computed(() => {
        const value = this.label();
        return typeof value === 'string' ? value : null;
    });
    protected readonly labelTranslationModel = computed(() => {
        const value = this.label();
        return typeof value === 'string' ? undefined : value;
    });
    protected readonly shouldShowErrors = computed(() => {
        const state = this.parentState();
        return state.invalid && state.touched;
    });
    protected readonly errorMessageKey = computed(() => {
        if (!this.shouldShowErrors()) {
            return null;
        }
        const errors = this.parentState().errors;
        if (errors?.['required']) {
            return 'shared.validation.required';
        }
        if (errors?.['countryRequired']) {
            return 'shared.validation.countryRequired';
        }
        if (errors?.['invalidPhone']) {
            return 'shared.validation.invalidPhone';
        }
        return null;
    });
    protected readonly parentErrorStateMatcher: ErrorStateMatcher = {
        isErrorState: () => this.shouldShowErrors(),
    };
    protected readonly countryMenuTrigger =
        viewChild<MatMenuTrigger>(MatMenuTrigger);

    private ngControl: NgControl | null = null;

    private readonly destroyRef = inject(DestroyRef);
    private readonly injector = inject(Injector);
    private readonly localizationService = inject(LocalizationService);
    private readonly phoneCountryService = inject(PhoneCountryService);
    private readonly translateService = inject(TranslateService);

    /**
     * Tracks the active translation language so the country list can be
     * re-localized and re-sorted reactively. Updated from the
     * `TranslateService.onLangChange` stream (see language-change wiring).
     */
    protected readonly currentLanguage = signal<string>(
        this.localizationService.getCurrentLanguage()
    );

    /**
     * Snapshot of the shared localized-country map, used as the default
     * source of country names when the caller does not provide the
     * `countryList` input.
     */
    private readonly defaultLocalizedCountries = toSignal(
        this.localizationService.localizedCountries$,
        { initialValue: new Map<string, CountryModel[]>() }
    );

    private onChange: (value: string) => void = () => undefined;
    private onTouched: () => void = () => undefined;
    private onValidatorChange: () => void = () => undefined;
    private hasBoundParentControlState = false;

    constructor() {
        this.translateService.onLangChange
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ lang }) => this.currentLanguage.set(lang));

        effect(() => {
            const localizedNames = this.resolveLocalizedNames();
            this.countryOptions.set(
                this.phoneCountryService.buildDisplayList({
                    localizedNames,
                    language: this.currentLanguage(),
                    allowedCountries: this.allowedCountries(),
                    priorityCountries: this.priorityCountries(),
                })
            );
        });

        effect(() => {
            this.showPlaceholder();
            this.placeholder.set(this.resolvePlaceholder());
        });

        effect(() => {
            // Re-run parent validation immediately whenever skipValidation toggles.
            this.skipValidation();
            this.onValidatorChange();
        });

        this.nationalControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.onValidatorChange();
                // Defer state sync until validators finish updating control status/errors.
                queueMicrotask(() => this.syncParentState());
            });

        effect(() => {
            this.selectedCountryIso2();
            this.onValidatorChange();
            // Defer state sync so parent validation/error state is settled before reading it.
            queueMicrotask(() => this.syncParentState());
        });
    }

    public ngOnInit(): void {
        // Resolving `NgControl` lazily here (instead of via constructor
        // injection) breaks the NG0200 cycle between this component and the
        // `NG_VALIDATORS` multi-provider declared below `@Component`:
        // FormControlName would otherwise need to fully compose its
        // validator list while we're still mid-construction.
        const ngControl = this.injector.get(NgControl, null, {
            optional: true,
            self: true,
        });
        if (ngControl) {
            this.ngControl = ngControl;
            ngControl.valueAccessor = this;
            // Safe to invoke twice: bindParentControlState() is idempotent via hasBoundParentControlState.
            this.bindParentControlState();
            // FormControlName can attach the concrete control on a later turn.
            // Retry binding in a microtask in case NgControl.control is attached asynchronously.
            queueMicrotask(() => this.bindParentControlState());
        }
    }

    /**
     * Reflects incoming parent values into the internal UI controls. The
     * parser auto-detects the country when the input starts with `+`; if
     * detection fails or the detected country is not in `allowedCountries`
     * we fall back to surfacing the raw digits so users can still edit
     * legacy values that were stored without a country code.
     */
    public writeValue(value: string | null): void {
        if (!value) {
            this.selectedCountryIso2.set(null);
            this.nationalControl.setValue('');
            return;
        }

        const parsed = parsePhoneNumberFromString(value);
        if (parsed?.country && this.isCountryAllowed(parsed.country)) {
            this.selectedCountryIso2.set(parsed.country.toLowerCase());
            this.nationalControl.setValue(parsed.nationalNumber);
            return;
        }

        this.selectedCountryIso2.set(null);
        this.nationalControl.setValue(this.onlyDigits(value));
    }

    public registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled.set(isDisabled);
        if (isDisabled) {
            this.nationalControl.disable({ emitEvent: false });
        } else {
            this.nationalControl.enable({ emitEvent: false });
        }
    }

    /**
     * Validates the parent control's emitted string. `required` is left to
     * the parent's own `Validators.required`; here we only surface phone-
     * specific problems:
     *
     * - `countryRequired`: the user entered digits without selecting a country
     * - `invalidPhone`: the composed `+<dialCode><digits>` is not a possible
     *    phone number for the selected country (length/prefix check via
     *    `isValidPhoneNumber` from libphonenumber-js/min)
     */
    public validate(control: AbstractControl): ValidationErrors | null {
        if (this.skipValidation()) {
            return null;
        }

        const raw: unknown = control.value;
        if (typeof raw !== 'string' || raw === '') {
            // Let the parent's `Validators.required` handle empty-state checks
            // and let transient states (country picked, no digits yet) pass
            // through without flagging an error.
            const digits = this.onlyDigits(this.nationalControl.value);
            if (digits.length > 0 && !this.selectedCountryIso2()) {
                return { countryRequired: true };
            }

            return null;
        }

        if (!raw.startsWith('+')) {
            return { countryRequired: true };
        }

        return isValidPhoneNumber(raw) ? null : { invalidPhone: true };
    }

    public registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }

    protected handleBlur(): void {
        queueMicrotask(() => {
            // Do not mark CVA as touched when country prefix is selected
            if (this.countryMenuTrigger()?.menuOpen) {
                return;
            }

            this.onTouched();
            this.syncParentState();
        });
    }

    protected handleCountrySelect(iso2: string): void {
        this.selectedCountryIso2.set(iso2);
        this.emitComposedValue();
    }

    protected handleNationalInput(value: string): void {
        // If the user pasted an international-looking string (starts with
        // `+` or `00`), let the parser re-detect the country so we don't
        // end up concatenating the country code twice in the emitted value.
        if (/^\s*(\+|00)/.test(value)) {
            const normalized = value.replace(/^\s*00/, '+');
            const parsed = parsePhoneNumberFromString(normalized);
            if (parsed?.country && this.isCountryAllowed(parsed.country)) {
                this.selectedCountryIso2.set(parsed.country.toLowerCase());
                this.nationalControl.setValue(parsed.nationalNumber);
                this.emitComposedValue();
                return;
            }
        }

        const digits = this.onlyDigits(value);
        if (digits !== this.nationalControl.value) {
            this.nationalControl.setValue(digits);
        }

        this.emitComposedValue();
    }

    protected handleClear(): void {
        this.selectedCountryIso2.set(null);
        this.nationalControl.setValue('');
        this.emitComposedValue();
        this.onTouched();
    }

    private emitComposedValue(): void {
        const iso2 = this.selectedCountryIso2();
        const digits = this.onlyDigits(this.nationalControl.value);
        const dialCode = iso2
            ? this.phoneCountryService.getDialCode(iso2 as CountryCode)
            : undefined;

        if (!dialCode || digits.length === 0) {
            this.onChange('');
            return;
        }

        this.onChange(`+${dialCode}${digits}`);
    }

    /**
     * Strips non-digit characters. Used to derive the digit-only tail of
     * the composed E.164 value and to sanitize raw pasted input before it
     * is mirrored back into the national field.
     */
    private onlyDigits(value: string): string {
        return value.replaceAll(/\D+/g, '');
    }

    private isCountryAllowed(country: CountryCode): boolean {
        const allowed = this.allowedCountries();
        if (!allowed) {
            return true;
        }
        return allowed.some(
            (code) => code.toLowerCase() === country.toLowerCase()
        );
    }

    /**
     * Resolves the localized country names used to label options. The caller
     * can override via the `countryList` input; otherwise the component reads
     * the shared `LocalizationService.localizedCountries$` snapshot for the
     * current language.
     */
    private resolveLocalizedNames(): CountryModel[] {
        const language = this.currentLanguage();
        const source = this.countryList() ?? this.defaultLocalizedCountries();
        return source.get(language) ?? [];
    }

    private bindParentControlState(): void {
        if (this.hasBoundParentControlState) {
            return;
        }

        const control = this.ngControl?.control;
        if (!control) {
            return;
        }

        this.hasBoundParentControlState = true;
        merge(control.statusChanges, control.valueChanges)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.syncParentState());

        this.syncParentState();
    }

    private syncParentState(): void {
        const control = this.ngControl?.control;
        if (!control) {
            this.parentState.set(INITIAL_PARENT_STATE);
            return;
        }
        this.parentState.set({
            invalid: control.invalid,
            touched: control.touched,
            dirty: control.dirty,
            errors: control.errors,
        });
    }

    /**
     * Returns a realistic national-format example for the selected country
     * (e.g. `091 234 5678` for Croatia) so the user gets a concrete hint of
     * the expected shape. Falls back to an empty string when no country is
     * selected or the library has no example for it.
     */
    private resolvePlaceholder(): string {
        if (!this.showPlaceholder()) {
            return '';
        }

        const iso2 = this.selectedCountryIso2();
        if (!iso2) {
            return '';
        }

        const example = getExampleNumber(
            iso2.toUpperCase() as CountryCode,
            mobileExamples
        );

        return example?.formatNational() ?? '';
    }
}
